/**
 * usePollBuilderTestCookie
 *
 * This custom hook monitors Builder.io A/B test cookies on a website and reports them to DataDog.
 *
 * Purpose:
 * - Detects when Builder.io sets test cookies in the browser
 * - Collects all active Builder.io tests for the current page
 * - Sends test information to DataDog for analytics and tracking
 * - Sets user identity in DataDog with Builder.io test data
 * - Works with Next.js client-side navigation
 *
 * Key features:
 * - Polls for cookies that appear after page render
 * - Ensures each page's tests are only reported once per session
 * - Handles SPA navigation by detecting route changes
 * - Prevents duplicate events during race conditions
 * - Efficiently cleans up resources (intervals, timeouts)
 * - Provides status indicators (isPolling, hasTests, etc.)
 *
 * Usage:
 * Only one instance of this hook should be called in the SiteContainer to monitor changes to Builder.io test cookies.
 * It automatically detects navigation and resets for new pages.
 *
 * Return values:
 * - isPolling: Whether the hook is actively looking for new Builder.io cookies
 * - builderObject: The collected Builder.io test information (sessionId and tests)
 * - hasTests: Whether any Builder.io tests were found on the current page
 * - currentPath: The current page path being monitored
 */

import { useEffect, useState, useRef, useCallback } from "react";
import Cookies from "js-cookie";
import { TrackingUtils } from "../utils/TrackingUtils";
import { useRouter } from "next/router";

interface BuilderObject {
    sessionId: string;
    tests: {
        [testId: string]: string; // key = testId, value = variantId
    };
}

interface UsePollBuilderTestCookieOptions {
    pollingDurationMs: number;
    pollingIntervalMs: number;
    ddActionName: string;
    onPollingEnd?: (found: boolean, builderObject?: BuilderObject) => void;
    isEnabled?: boolean;
    debugMode?: boolean;
    routeChangeDelayMs?: number;
}

interface UsePollBuilderTestCookieResult {
    isPolling: boolean;
    builderObject: BuilderObject | null;
    hasTests: boolean;
    currentPath: string;
}

interface PathTrackingState {
    hasReported: boolean;
    lastReportTime?: number;
}

export function usePollBuilderTestCookie({
    pollingDurationMs,
    pollingIntervalMs,
    ddActionName,
    onPollingEnd,
    isEnabled = true,
    debugMode = false,
    routeChangeDelayMs = 1000
}: UsePollBuilderTestCookieOptions): UsePollBuilderTestCookieResult {
    const router = useRouter();
    const { asPath } = router;

    const [builderObject, setBuilderObject] = useState<BuilderObject | null>(null);
    const [isPolling, setIsPolling] = useState<boolean>(false);
    const [hasTests, setHasTests] = useState<boolean>(false);
    const [currentPath, setCurrentPath] = useState<string>(asPath);
    const [pathTracking, setPathTracking] = useState<Record<string, PathTrackingState>>({});
    const [forcePollingCounter, setForcePollingCounter] = useState(0);
    const [shouldResetForPath, setShouldResetForPath] = useState<string | null>(null);

    const processingPathsRef = useRef<Set<string>>(new Set());
    const reportedPathsRef = useRef<Set<string>>(new Set());
    const onPollingEndRef = useRef(onPollingEnd);
    const ddActionNameRef = useRef(ddActionName);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const routeChangeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const initialCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Update refs when props change
    useEffect(() => {
        onPollingEndRef.current = onPollingEnd;
    }, [onPollingEnd]);

    useEffect(() => {
        ddActionNameRef.current = ddActionName;
    }, [ddActionName]);

    const log = useCallback(
        (message: string, ...args: any[]) => {
            if (debugMode) {
                console.log(`[PollBuilderCookie] ${message}`, ...args);
            }
        },
        [debugMode]
    );

    // Listen to router events for navigation detection
    useEffect(() => {
        const handleRouteChangeStart = (url: string) => {
            log(`Route change started to: ${url}`);
        };

        const handleRouteChangeComplete = (url: string) => {
            log(`Route change completed to: ${url}`);

            // Clear any existing timeouts
            if (routeChangeTimeoutRef.current) {
                clearTimeout(routeChangeTimeoutRef.current);
            }

            // We'll update the current path immediately
            setCurrentPath(url);

            // But delay the reset of other state to allow Builder.io to initialize
            routeChangeTimeoutRef.current = setTimeout(() => {
                log(`Processing delayed route change to: ${url}`);

                // Mark this path as needing reset
                setShouldResetForPath(url);

                routeChangeTimeoutRef.current = null;
            }, routeChangeDelayMs);
        };

        router.events.on("routeChangeStart", handleRouteChangeStart);
        router.events.on("routeChangeComplete", handleRouteChangeComplete);

        return () => {
            router.events.off("routeChangeStart", handleRouteChangeStart);
            router.events.off("routeChangeComplete", handleRouteChangeComplete);

            if (routeChangeTimeoutRef.current) {
                clearTimeout(routeChangeTimeoutRef.current);
            }
        };
    }, [router.events, log, routeChangeDelayMs]);

    // Handle path changes that need reset (triggered by shouldResetForPath)
    useEffect(() => {
        if (shouldResetForPath === null) return;

        log(`Resetting state for path: ${shouldResetForPath}`);

        // Clean up any existing polling
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }

        if (pollingTimeoutRef.current) {
            clearTimeout(pollingTimeoutRef.current);
            pollingTimeoutRef.current = null;
        }

        // Reset all state for this path
        setIsPolling(false);
        setBuilderObject(null);
        setHasTests(false);

        // Then trigger a new polling cycle
        log(`Forcing new polling cycle for path: ${shouldResetForPath}`);
        setForcePollingCounter(prev => prev + 1);

        // Done with this reset
        setShouldResetForPath(null);
    }, [shouldResetForPath, log]);

    // Collect all builder test cookies and build the complete builder object
    const constructBuilderObject = useCallback((): BuilderObject => {
        const sessionId = Cookies.get("builderSessionId") ?? "";
        const allCookies = Cookies.get();
        const tests: { [testId: string]: string } = {}; // Object instead of array
        let foundTestCount = 0;

        for (const name in allCookies) {
            if (name.startsWith("builder.tests.")) {
                const testId = name.replace("builder.tests.", "");
                const variantId = allCookies[name];
                if (variantId) {
                    // Add as property instead of pushing to array
                    tests[testId] = variantId;
                    foundTestCount++;
                    log(`Found builder test cookie: ${name}=${variantId}`);
                }
            }
        }

        log(`Total builder tests found: ${foundTestCount}`);
        return { sessionId, tests };
    }, [log]);

    // Process all builder test cookies and send the collected data to DataDog
    const processBuilderCookies = useCallback(async () => {
        // Check if we're already processing this path to prevent duplicate calls
        if (processingPathsRef.current.has(currentPath)) {
            log(`Already processing ${currentPath}, skipping duplicate check`);
            return false;
        }

        // Mark that we're processing this path
        processingPathsRef.current.add(currentPath);

        log(`Checking for Builder cookies on path: ${currentPath}`);
        const currentBuilderObject = constructBuilderObject();
        const testsCount = Object.keys(currentBuilderObject.tests).length;

        if (testsCount === 0) {
            log(`No builder test cookies found on path: ${currentPath}`);
            setBuilderObject(null);
            setHasTests(false);
            setIsPolling(false);

            // Clear processing marker when done
            processingPathsRef.current.delete(currentPath);

            if (onPollingEndRef.current) {
                onPollingEndRef.current(false);
            }
            return false;
        }

        log(`Processing ${testsCount} builder test cookies on path: ${currentPath}`);
        setBuilderObject(currentBuilderObject);
        setHasTests(true);
        setIsPolling(false);

        // Check if we've already reported for this path using both ref (immediate) and state (persistent)
        const pathState = pathTracking[currentPath];
        const hasReportedForPath = reportedPathsRef.current.has(currentPath) || (pathState && pathState.hasReported);

        if (!hasReportedForPath) {
            log(`No previous report for path ${currentPath}, sending DataDog action`);

            // Mark as reported IMMEDIATELY using the ref
            reportedPathsRef.current.add(currentPath);

            // Also update state for persistence
            setPathTracking(prev => ({
                ...prev,
                [currentPath]: {
                    hasReported: true,
                    lastReportTime: Date.now()
                }
            }));

            const analyticsPayload = {
                builder: currentBuilderObject
            };

            try {
                await TrackingUtils.setDataDogUser({ builder: currentBuilderObject });
                log(`Set DataDog user with builder data for path: ${currentPath}`);

                TrackingUtils.sendDataDogAction(ddActionNameRef.current, analyticsPayload);
                log(`Sent DataDog action: ${ddActionNameRef.current} for path: ${currentPath}`);
            } catch (error) {
                log(`Error in DataDog reporting: ${error}`);
            }
        } else {
            log(`DataDog action already sent for path ${currentPath}, skipping duplicate send`);
        }

        if (onPollingEndRef.current) {
            onPollingEndRef.current(true, currentBuilderObject);
        }

        // Clear processing marker when done
        processingPathsRef.current.delete(currentPath);
        return true;
    }, [constructBuilderObject, log, currentPath, pathTracking]);

    // Main polling effect - runs on forcePollingCounter changes
    useEffect(() => {
        if (!isEnabled || typeof window === "undefined") {
            return;
        }

        log(`Starting polling cycle #${forcePollingCounter} on path: ${currentPath}`);
        setIsPolling(true);

        // Initial check with short delay to let Builder initialize
        const checkWithDelay = () => {
            initialCheckTimeoutRef.current = setTimeout(async () => {
                const found = await processBuilderCookies();
                if (found) {
                    log(`Builder cookies found for path: ${currentPath}`);
                } else {
                    // Start interval polling if nothing found
                    log(`Starting interval polling for path ${currentPath}. Duration: ${pollingDurationMs}ms, Interval: ${pollingIntervalMs}ms`);

                    pollingIntervalRef.current = setInterval(async () => {
                        const found = await processBuilderCookies();
                        if (found) {
                            if (pollingIntervalRef.current) {
                                clearInterval(pollingIntervalRef.current);
                                pollingIntervalRef.current = null;
                            }

                            if (pollingTimeoutRef.current) {
                                clearTimeout(pollingTimeoutRef.current);
                                pollingTimeoutRef.current = null;
                            }
                        }
                    }, pollingIntervalMs);

                    // Set overall polling timeout
                    pollingTimeoutRef.current = setTimeout(async () => {
                        log(`Polling timeout reached for path: ${currentPath}`);

                        if (pollingIntervalRef.current) {
                            clearInterval(pollingIntervalRef.current);
                            pollingIntervalRef.current = null;
                        }

                        // Final check before giving up
                        const found = await processBuilderCookies();
                        if (!found) {
                            log(`Polling ended for path ${currentPath}. No builder test cookies found within duration.`);
                            setIsPolling(false);

                            if (onPollingEndRef.current) {
                                onPollingEndRef.current(false);
                            }
                        }
                    }, pollingDurationMs);
                }
            }, 250); // Short delay to let Builder initialize
        };

        checkWithDelay();

        // Cleanup function
        return () => {
            if (initialCheckTimeoutRef.current) {
                clearTimeout(initialCheckTimeoutRef.current);
                initialCheckTimeoutRef.current = null;
            }

            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }

            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current);
                pollingTimeoutRef.current = null;
            }

            log(`Cleaning up polling effect for path: ${currentPath}`);
        };
    }, [isEnabled, pollingDurationMs, pollingIntervalMs, log, processBuilderCookies, currentPath, forcePollingCounter]);

    return { isPolling, builderObject, hasTests, currentPath };
}
